// Per-action RBAC for the admin panel. Mirrors the backend exactly — see
// src/constants/rolePermissions.js (roleHasPermission) and
// src/middlewares/auth.middleware.js (hasPermission) on the backend. A
// role's permissions value, as returned by GET /api/v1/admin/my-permissions,
// is either:
//   - the sentinel array ['*']            → full access to every module/action
//   - an object map { [moduleKey]: actionKey[] } → granular, per-action access
//
// Never hardcode a role name or re-derive this logic elsewhere — every
// permission check in the app should go through hasPermission (or the
// usePermission hook, which wraps it) so frontend and backend never drift.
export const hasPermission = (permissions, moduleKey, action) => {
  if (!permissions) return false;
  if (Array.isArray(permissions)) return permissions.includes('*');
  const moduleActions = permissions[moduleKey];
  return Array.isArray(moduleActions) && moduleActions.includes(action);
};

// A module is visible (sidebar link shown, route reachable) if the role has
// ANY action on it at all — even a role with only ['read'] on a module
// should see that module's page, just without its action buttons.
export const canAccessModule = (permissions, moduleKey) => {
  if (!permissions) return false;
  if (Array.isArray(permissions)) return permissions.includes('*');
  const moduleActions = permissions[moduleKey];
  return Array.isArray(moduleActions) && moduleActions.length > 0;
};

// Sidebar groups/collapses in routes.js double as modules (their own `key`
// already matches a permission string, e.g. "shop-management"). Group
// children and the detailed `type: 'route'` entries further down routes.js
// use their own, finer-grained keys (e.g. "categories", "value-pack-create"),
// so this map resolves each of those back to the owning module.
//
// env-variables and account-management are each their OWN module in the
// backend catalog (with their own action lists, including env-variables'
// reveal/lock) — do not lump them under "settings" the way earlier,
// module-level-only gating did.
export const MODULE_KEY_MAP = {
  // Dashboard is a landing page shown to every authenticated role.
  dashboard: 'dashboard',

  // Page Management
  'page-management': 'page-management',
  pages: 'page-management',
  'page-sections-builder': 'page-management',
  'page-versions': 'page-management',
  'section-content-editor': 'page-management',
  'legal-pages': 'page-management',
  'section-templates': 'page-management',

  // Email Management
  'email-management': 'email-management',
  'email-templates': 'email-management',
  'reminder-settings': 'email-management',
  'admin-notifications': 'email-management',

  // Shop Management
  'shop-management': 'shop-management',
  categories: 'shop-management',
  products: 'shop-management',
  valuepack: 'shop-management',
  'subscription-type': 'shop-management',
  'understand-it-better': 'shop-management',
  design: 'shop-management',

  // Leads Management
  'leads-management': 'leads-management',
  'contact-inquiry': 'leads-management',
  newsletter: 'leads-management',

  // Orders
  orders: 'orders',

  // Q&A Center
  'qa-center': 'qa-center',
  'question-answers': 'qa-center',
  'questionnaire-submissions': 'qa-center',

  // Standalone modules
  'data-import': 'data-import',
  'case-studies': 'case-studies',
  promocodes: 'promocodes',
  billing: 'billing',
  customers: 'customers',
  tickets: 'tickets',
  'error-logs': 'error-logs',
  'activity-logs': 'activity-logs',

  // Settings — invoice/other/filters are the only sub-pages still under the
  // generic "settings" module. env-variables and account-management are
  // split out as their own modules (see comment above).
  settings: 'settings',
  'invoice-settings': 'settings',
  'missed-payment-settings': 'settings',
  'other-settings': 'settings',
  'dynamic-filters': 'settings',
  'env-variables': 'env-variables',
  'account-management': 'account-management',
};

// Personal/global utilities every authenticated admin may always access,
// regardless of role. Profile is intentionally here (rather than being a
// role permission): an admin must always be able to view and edit their own
// account from the navbar. The remaining navbar controls (logout,
// configurator and notifications) are client-side utilities and have no RBAC
// checks attached to them.
const ALWAYS_ALLOWED_MODULES = new Set(['dashboard', 'profile']);

// Modules reserved for admin/superAdmin accounts regardless of any role's
// permissions (even a full-access custom role). Mirrors the backend, where
// these routes use isAdminOrSuperAdmin instead of hasPermission.
export const ADMIN_ONLY_MODULES = new Set(['env-variables', 'account-management']);

export const getModuleKeyForRouteKey = (routeKey) => MODULE_KEY_MAP[routeKey] || routeKey;

// Page/route visibility — "can this role see this module at all" (any
// action). Use this for the sidebar and for ProtectedRoute's routeKey check.
export const isModuleAllowed = (moduleKey, permissions = [], hasFullAccess = false, isSystemAdmin = false) => {
  if (ADMIN_ONLY_MODULES.has(moduleKey)) return isSystemAdmin;
  if (hasFullAccess) return true;
  if (!moduleKey) return true; // ungated entries (e.g. profile, auth pages)
  if (ALWAYS_ALLOWED_MODULES.has(moduleKey)) return true;
  return canAccessModule(permissions, moduleKey);
};

export const isRouteKeyAllowed = (routeKey, permissions = [], hasFullAccess = false, isSystemAdmin = false) =>
  isModuleAllowed(getModuleKeyForRouteKey(routeKey), permissions, hasFullAccess, isSystemAdmin);

// Action-level check for a route key — resolves the route key to its module
// first, then checks the specific action. Use this in route guards for
// create/edit pages (e.g. routeKey="promocodes", action="create").
export const isActionAllowedForRouteKey = (routeKey, action, permissions = [], hasFullAccess = false, isSystemAdmin = false) => {
  const moduleKey = getModuleKeyForRouteKey(routeKey);
  if (ADMIN_ONLY_MODULES.has(moduleKey)) return isSystemAdmin;
  if (hasFullAccess) return true;
  return hasPermission(permissions, moduleKey, action);
};

// Filters the sidebar/route config so a restricted role only sees groups,
// collapses and group-children for modules it has any access to. Used for
// rendering the sidebar; route guarding in App.js checks every route
// (visible or not) independently so a direct URL can't bypass this.
export const filterRoutesByPermissions = (routes, permissions = [], hasFullAccess = false, isSystemAdmin = false) => {
  if (hasFullAccess && isSystemAdmin) return routes;

  return routes.reduce((acc, item) => {
    if (item.type === 'group') {
      if (!isRouteKeyAllowed(item.key, permissions, hasFullAccess, isSystemAdmin)) return acc;
      const children = (item.children || []).filter((child) =>
        isRouteKeyAllowed(child.key, permissions, hasFullAccess, isSystemAdmin),
      );
      if (!children.length) return acc;
      acc.push({ ...item, children });
      return acc;
    }

    if (item.type === 'collapse') {
      if (!isRouteKeyAllowed(item.key, permissions, hasFullAccess, isSystemAdmin)) return acc;
      acc.push(item);
      return acc;
    }

    // 'route', 'title', 'divider' and anything else pass through unchanged —
    // the sidebar renderer only draws from 'group'/'collapse'/'title'/'divider',
    // and route-level access is enforced separately by the route guard.
    acc.push(item);
    return acc;
  }, []);
};
